import React from 'react';

import { Alert, Box, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { PromoOfferMetric } from '~/services/PromoService';

import DebugModal from '../DebugModal/DebugModal';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: true, // Enable sorting
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 1,
};

const getColumns = (): Array<GridColDef<PromoOfferMetric>> => [
  {
    field: 'offerId',
    headerName: 'Offer ID',
    ...sharedOpts,
    flex: 1.5,
    sortable: false,
  },
  {
    field: 'mpv',
    headerName: 'MPV',
    ...sharedOpts,
    renderCell: (params) => (
      <Typography>{typeof params.value === 'number' ? params.value.toLocaleString() : params.value}</Typography>
    ),
  },
  {
    field: 'region',
    headerName: 'Region',
    ...sharedOpts,
    flex: 1,
    sortable: false,
  },
  {
    field: 'brand',
    headerName: 'Brand',
    ...sharedOpts,
    flex: 1,
    sortable: false,
  },
  {
    field: 'mpvNoPromo7d',
    headerName: 'MPV No Promo 7d',
    ...sharedOpts,
    flex: 1,
  },
  {
    field: 'conversionNoPromo7d',
    headerName: 'Conversion No Promo 7d',
    ...sharedOpts,
    flex: 1,
  },
  {
    field: 'conversionPromo7d',
    headerName: 'Conversion Promo 7d',
    ...sharedOpts,
    flex: 1,
  },
  {
    field: 'totalOrders7d',
    headerName: 'Total Orders 7d',
    ...sharedOpts,
    flex: 1,
  },
  {
    field: 'totalMargin7d',
    headerName: 'Total Margin 7d',
    ...sharedOpts,
    flex: 1,
  },
  {
    field: 'totalSales7d',
    headerName: 'Total Sales 7d',
    ...sharedOpts,
    flex: 1,
  },
  {
    field: 'totalOrdersNoPromo7d',
    headerName: 'Total Orders No Promo 7d',
    ...sharedOpts,
    flex: 1,
  },
  {
    field: 'dateCreated',
    headerName: 'Created At',
    ...sharedOpts,
    flex: 1,
    renderCell: (params) => {
      if ('dateCreated' in params.row) {
        return <Typography>{new Date(params.row.dateCreated).toLocaleString()}</Typography>;
      }
      return <Alert severity="error">No date created</Alert>;
    },
  },
  {
    field: 'dateUpdated',
    headerName: 'Updated At',
    ...sharedOpts,
    flex: 1,
    renderCell: (params) => {
      if ('dateUpdated' in params.row) {
        return <Typography>{new Date(params.row.dateUpdated).toLocaleString()}</Typography>;
      }
      return <Alert severity="error">No date updated</Alert>;
    },
  },
  {
    field: 'debug',
    headerName: 'Debug',
    ...sharedOpts,
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      return <DebugModal type="generic" data={params.row} />;
    },
  },
];

type OfferMetricsGridProps = {
  data: PromoOfferMetric[];
  isLoading: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  currentSortBy: string;
  currentSortOrder: 'asc' | 'desc';
};

export default function OfferMetricsGrid({
  data,
  isLoading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onSortChange,
  currentSortBy,
  currentSortOrder,
}: OfferMetricsGridProps) {
  const dataRows = data.map((row, index) => ({
    id: index,
    ...row,
  }));

  // Convert API field names to DataGrid field names
  const fieldMapping: Record<string, string> = {
    updated_at: 'dateUpdated',
    created_at: 'dateCreated',
    offer_id: 'offerId',
    mpv_no_promo_7d: 'mpvNoPromo7d',
    conversion_no_promo_7d: 'conversionNoPromo7d',
    conversion_promo_7d: 'conversionPromo7d',
    total_orders_7d: 'totalOrders7d',
    total_margin_7d: 'totalMargin7d',
    total_sales_7d: 'totalSales7d',
    total_orders_no_promo_7d: 'totalOrdersNoPromo7d',
    total_orders_promo_7d: 'totalOrdersPromo7d',
    margin_no_promo_7d: 'marginNoPromo7d',
    margin_diff_7d: 'marginDiff7d',
  };

  // Convert DataGrid field names back to API field names
  const reverseFieldMapping: Record<string, string> = Object.entries(fieldMapping).reduce(
    (acc, [apiField, gridField]) => {
      acc[gridField] = apiField;
      return acc;
    },
    {} as Record<string, string>,
  );

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      const { field, sort } = model[0];
      const apiField = reverseFieldMapping[field] || field;
      onSortChange(apiField, sort || 'asc');
    }
  };

  // Convert API sort field to DataGrid field for initial state
  const initialSortField = fieldMapping[currentSortBy] || currentSortBy;

  return (
    <Box mt={2}>
      <DataGrid
        slots={{ pagination: GridPagination }}
        onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
        onSortModelChange={handleSortModelChange}
        loading={isLoading}
        rows={dataRows}
        columns={getColumns()}
        autoHeight
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={{ page, pageSize }}
        rowHeight={60}
        disableRowSelectionOnClick
        initialState={{
          pagination: {
            paginationModel: { page, pageSize },
          },
          sorting: {
            sortModel: [{ field: initialSortField, sort: currentSortOrder }],
          },
        }}
      />
    </Box>
  );
}
